/* crm-commerce.jsx — Phase 5: Product Catalog (27), Price Books (28), Quote Foundation (29)
   Bridged component — no imports, all via window globals. */

const { useState, useEffect, useMemo, useCallback, useRef } = React;

/* ── helpers ── */
const _fmt = (n, currency = 'USD') => {
  if (n == null) return '—';
  try { return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 2 }).format(Number(n)); }
  catch { return `$${Number(n).toFixed(2)}`; }
};
const _date = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const card = { background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', borderRadius: 10 };
const inputSx = { padding: '6px 10px', border: '1px solid var(--nx-border)', borderRadius: 6, background: 'var(--nx-bg)', color: 'var(--nx-text)', fontSize: 12, width: '100%', boxSizing: 'border-box' };
const btnPrimary = { padding: '6px 14px', borderRadius: 6, border: 'none', background: 'var(--nx-accent)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const btnGhost = { padding: '6px 12px', borderRadius: 6, border: '1px solid var(--nx-border)', background: 'transparent', color: 'var(--nx-text)', fontSize: 12, cursor: 'pointer' };

const QUOTE_STATUSES = ['DRAFT', 'SENT', 'APPROVED', 'REJECTED', 'EXPIRED'];
const STATUS_COLORS  = { DRAFT: '#6b7280', SENT: '#3b82f6', APPROVED: '#10b981', REJECTED: '#ef4444', EXPIRED: '#f59e0b' };
const CURRENCIES     = ['USD', 'EUR', 'GBP', 'JPY', 'INR', 'AUD', 'CAD'];

const StatusPill = ({ status }) => {
  const c = STATUS_COLORS[status] || '#6b7280';
  return <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: c + '22', color: c }}>{status}</span>;
};

/* ══════════════════════════════════════════════════════
   ProductCatalogView
   ══════════════════════════════════════════════════════ */
const ProductCatalogView = () => {
  const wsId = window.__nx_ws?.id || window.__nx_ws?.workspaceId;
  const toast = useToast();
  // FIX-01: detect whether the workspace product catalog API is available.
  // dealEnrichment.listProducts(wsId, dealId) is deal-specific and cannot substitute.
  // When getProducts is absent the catalog is not implemented — show controlled state.
  const catalogReady = typeof window.__nx_api?.dealEnrichment?.getProducts === 'function';
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(catalogReady);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [newP, setNewP] = useState({ name: '', sku: '', category: '', price: '', description: '' });
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    if (!catalogReady) return;
    setLoading(true);
    window.__nx_api.dealEnrichment.getProducts(wsId)
      .then(r => setProducts(Array.isArray(r) ? r : r?.data || []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [wsId, catalogReady]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!search) return products;
    const q = search.toLowerCase();
    return products.filter(p => (p.name || '').toLowerCase().includes(q) || (p.sku || '').toLowerCase().includes(q) || (p.category || '').toLowerCase().includes(q));
  }, [products, search]);

  const createProduct = async () => {
    if (!newP.name) { toast.show({ title: 'Product name required', type: 'error' }); return; }
    setCreating(true);
    try {
      await window.__nx_api?.dealEnrichment?.createProduct?.(wsId, { ...newP, price: Number(newP.price) });
      toast.show({ title: 'Product created', type: 'success' });
      setShowForm(false); setNewP({ name: '', sku: '', category: '', price: '', description: '' }); load();
    } catch (e) { toast.show({ title: e.message, type: 'error' }); }
    setCreating(false);
  };

  // FIX-02/FIX-03: controlled unavailable state — all hooks called above (Rules of Hooks satisfied).
  // CRM shell (sidebar, navigation) remains fully alive. User can navigate to any other module.
  if (!catalogReady) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 16, padding: '60px 24px', boxSizing: 'border-box' }}>
        <div style={{ fontSize: 48, opacity: 0.25, userSelect: 'none', lineHeight: 1 }}>📦</div>
        <div style={{ textAlign: 'center', maxWidth: 400 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--nx-text)', marginBottom: 8 }}>Product Catalog</div>
          <div style={{ fontSize: 14, color: 'var(--nx-text-muted)', lineHeight: 1.6 }}>
            Product Catalog is not yet available in this workspace.
            This feature will be released in an upcoming update.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <span style={{ fontWeight: 700, fontSize: 15 }}>Product Catalog</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <input style={{ ...inputSx, width: 200 }} placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)} />
          <button style={btnPrimary} onClick={() => setShowForm(s => !s)}>+ Add Product</button>
        </div>
      </div>

      {showForm && (
        <div style={{ ...card, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 13 }}>New Product</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            <input style={inputSx} placeholder="Product name *" value={newP.name} onChange={e => setNewP(p => ({ ...p, name: e.target.value }))} />
            <input style={inputSx} placeholder="SKU" value={newP.sku} onChange={e => setNewP(p => ({ ...p, sku: e.target.value }))} />
            <input style={inputSx} placeholder="Category" value={newP.category} onChange={e => setNewP(p => ({ ...p, category: e.target.value }))} />
            <input type="number" style={inputSx} placeholder="Unit price" value={newP.price} onChange={e => setNewP(p => ({ ...p, price: e.target.value }))} />
            <input style={{ ...inputSx, gridColumn: 'span 2' }} placeholder="Description" value={newP.description} onChange={e => setNewP(p => ({ ...p, description: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={btnGhost} onClick={() => setShowForm(false)}>Cancel</button>
            <button style={btnPrimary} onClick={createProduct} disabled={creating}>{creating ? 'Creating...' : 'Create'}</button>
          </div>
        </div>
      )}

      {loading ? <div style={{ color: 'var(--nx-text-muted)', fontSize: 13 }}>Loading products...</div> : (
        <div style={{ ...card, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: 'var(--nx-surface)' }}>
                {['Name', 'SKU', 'Category', 'Price', 'Description'].map(h => (
                  <th key={h} style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid var(--nx-border)', color: 'var(--nx-text-muted)', fontSize: 10, fontWeight: 700 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: 'var(--nx-text-muted)' }}>No products found</td></tr>
              ) : filtered.map(p => (
                <tr key={p.id} style={{ borderBottom: '1px solid var(--nx-border)' }}>
                  <td style={{ padding: '8px 12px', fontWeight: 600 }}>{p.name}</td>
                  <td style={{ padding: '8px 12px', color: 'var(--nx-text-muted)' }}>{p.sku || '—'}</td>
                  <td style={{ padding: '8px 12px' }}>{p.category || '—'}</td>
                  <td style={{ padding: '8px 12px', fontWeight: 600 }}>{_fmt(p.price)}</td>
                  <td style={{ padding: '8px 12px', color: 'var(--nx-text-muted)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.description || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════
   PriceBookCard
   ══════════════════════════════════════════════════════ */
const PriceBookCard = ({ pb, onView }) => (
  <div style={{ ...card, padding: 14, display: 'flex', flexDirection: 'column', gap: 8, cursor: 'pointer' }} onClick={() => onView?.(pb)}>
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
      <div>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>{pb.name}</div>
        <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>{pb.currency} · {(pb.entries || []).length} entries</div>
      </div>
      {pb.isDefault && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: 'var(--nx-accent-tint-2)', color: 'var(--nx-accent)' }}>Default</span>}
    </div>
    {(pb.validFrom || pb.validTo) && (
      <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>{_date(pb.validFrom)} – {_date(pb.validTo)}</div>
    )}
  </div>
);

/* ══════════════════════════════════════════════════════
   PriceBookEntryRow
   ══════════════════════════════════════════════════════ */
const PriceBookEntryRow = ({ entry, currency = 'USD', onRemove }) => (
  <tr style={{ borderBottom: '1px solid var(--nx-border)' }}>
    <td style={{ padding: '7px 12px', fontSize: 12, fontWeight: 600 }}>{entry.productName || entry.product?.name || '—'}</td>
    <td style={{ padding: '7px 12px', fontSize: 12 }}>{_fmt(entry.unitPrice, currency)}</td>
    <td style={{ padding: '7px 12px', fontSize: 12 }}>{entry.discount != null ? `${entry.discount}%` : '—'}</td>
    <td style={{ padding: '7px 12px', fontSize: 12, color: 'var(--nx-text-muted)' }}>{entry.minQty ?? 1} – {entry.maxQty ?? '∞'}</td>
    {onRemove && <td style={{ padding: '7px 12px' }}><button onClick={onRemove} style={{ ...btnGhost, padding: '2px 6px', color: '#ef4444', fontSize: 11 }}>Remove</button></td>}
  </tr>
);

/* ══════════════════════════════════════════════════════
   PriceBookForm
   ══════════════════════════════════════════════════════ */
const PriceBookForm = ({ onSave, onCancel }) => {
  const wsId = window.__nx_ws?.id || window.__nx_ws?.workspaceId;
  const toast = useToast();
  const [f, setF] = useState({ name: '', currency: 'USD', isDefault: false, validFrom: '', validTo: '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.name) { toast.show({ title: 'Name required', type: 'error' }); return; }
    setSaving(true);
    try {
      await window.__nx_api?.priceBooks?.create(wsId, f);
      toast.show({ title: 'Price book created', type: 'success' });
      onSave?.();
    } catch (e) { toast.show({ title: e.message, type: 'error' }); }
    setSaving(false);
  };

  return (
    <div style={{ ...card, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontWeight: 700, fontSize: 13 }}>New Price Book</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div><label style={{ fontSize: 11, color: 'var(--nx-text-muted)', display: 'block', marginBottom: 3 }}>Name *</label><input style={inputSx} value={f.name} onChange={e => set('name', e.target.value)} /></div>
        <div><label style={{ fontSize: 11, color: 'var(--nx-text-muted)', display: 'block', marginBottom: 3 }}>Currency</label>
          <select style={inputSx} value={f.currency} onChange={e => set('currency', e.target.value)}>{CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
        <div><label style={{ fontSize: 11, color: 'var(--nx-text-muted)', display: 'block', marginBottom: 3 }}>Valid From</label><input type="date" style={inputSx} value={f.validFrom} onChange={e => set('validFrom', e.target.value)} /></div>
        <div><label style={{ fontSize: 11, color: 'var(--nx-text-muted)', display: 'block', marginBottom: 3 }}>Valid To</label><input type="date" style={inputSx} value={f.validTo} onChange={e => set('validTo', e.target.value)} /></div>
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
        <input type="checkbox" checked={f.isDefault} onChange={e => set('isDefault', e.target.checked)} /> Set as default price book
      </label>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        {onCancel && <button style={btnGhost} onClick={onCancel}>Cancel</button>}
        <button style={btnPrimary} onClick={submit} disabled={saving}>{saving ? 'Creating...' : 'Create'}</button>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════
   PriceBookDetailView
   ══════════════════════════════════════════════════════ */
const PriceBookDetailView = ({ pb, onBack }) => {
  const wsId = window.__nx_ws?.id || window.__nx_ws?.workspaceId;
  const toast = useToast();
  const [entries, setEntries] = useState(pb.entries || []);
  const [showAdd, setShowAdd] = useState(false);
  const [newE, setNewE] = useState({ productName: '', unitPrice: '', discount: '', minQty: '', maxQty: '' });
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    window.__nx_api?.priceBooks?.getEntries(wsId, pb.id)
      .then(r => setEntries(Array.isArray(r) ? r : r?.data || []))
      .catch(() => {});
  }, [pb.id, wsId]);

  const addEntry = async () => {
    if (!newE.productName || !newE.unitPrice) { toast.show({ title: 'Product name and unit price required', type: 'error' }); return; }
    setAdding(true);
    try {
      const e = await window.__nx_api?.priceBooks?.addEntry(wsId, pb.id, { ...newE, unitPrice: Number(newE.unitPrice), discount: newE.discount ? Number(newE.discount) : null, minQty: newE.minQty ? Number(newE.minQty) : 1, maxQty: newE.maxQty ? Number(newE.maxQty) : null });
      setEntries(prev => [...prev, e || { ...newE, id: Date.now() }]);
      setNewE({ productName: '', unitPrice: '', discount: '', minQty: '', maxQty: '' });
      setShowAdd(false);
      toast.show({ title: 'Entry added', type: 'success' });
    } catch (e) { toast.show({ title: e.message, type: 'error' }); }
    setAdding(false);
  };

  const removeEntry = async (id) => {
    try {
      await window.__nx_api?.priceBooks?.removeEntry(wsId, pb.id, id);
      setEntries(e => e.filter(x => x.id !== id));
    } catch (err) { toast.show({ title: err.message, type: 'error' }); }
  };

  return (
    <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button style={{ ...btnGhost, padding: '4px 10px', fontSize: 11 }} onClick={onBack}><NexusIcon name="ChevronLeft" size={12} /> Back</button>
        <span style={{ fontWeight: 700, fontSize: 15 }}>{pb.name}</span>
        {pb.isDefault && <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: 'var(--nx-accent-tint-2)', color: 'var(--nx-accent)' }}>Default</span>}
        <span style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginLeft: 'auto' }}>{pb.currency}</span>
      </div>
      <div style={{ ...card, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--nx-surface)' }}>
              {['Product', 'Unit Price', 'Discount', 'Qty Range', ''].map(h => (
                <th key={h} style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid var(--nx-border)', color: 'var(--nx-text-muted)', fontSize: 10, fontWeight: 700 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 ? (
              <tr><td colSpan={5} style={{ padding: 20, textAlign: 'center', color: 'var(--nx-text-muted)', fontSize: 12 }}>No entries yet</td></tr>
            ) : entries.map(e => <PriceBookEntryRow key={e.id} entry={e} currency={pb.currency} onRemove={() => removeEntry(e.id)} />)}
          </tbody>
        </table>
      </div>
      {showAdd ? (
        <div style={{ ...card, padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontWeight: 700, fontSize: 12 }}>Add Entry</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
            <input style={inputSx} placeholder="Product name *" value={newE.productName} onChange={e => setNewE(p => ({ ...p, productName: e.target.value }))} />
            <input type="number" style={inputSx} placeholder="Unit price *" value={newE.unitPrice} onChange={e => setNewE(p => ({ ...p, unitPrice: e.target.value }))} />
            <input type="number" style={inputSx} placeholder="Discount %" value={newE.discount} onChange={e => setNewE(p => ({ ...p, discount: e.target.value }))} />
            <input type="number" style={inputSx} placeholder="Min qty" value={newE.minQty} onChange={e => setNewE(p => ({ ...p, minQty: e.target.value }))} />
            <input type="number" style={inputSx} placeholder="Max qty" value={newE.maxQty} onChange={e => setNewE(p => ({ ...p, maxQty: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={btnGhost} onClick={() => setShowAdd(false)}>Cancel</button>
            <button style={btnPrimary} onClick={addEntry} disabled={adding}>{adding ? 'Adding...' : 'Add Entry'}</button>
          </div>
        </div>
      ) : <button style={{ ...btnGhost, alignSelf: 'flex-start' }} onClick={() => setShowAdd(true)}>+ Add Entry</button>}
    </div>
  );
};

/* ══════════════════════════════════════════════════════
   PriceBooksView
   ══════════════════════════════════════════════════════ */
const PriceBooksView = () => {
  const wsId = window.__nx_ws?.id || window.__nx_ws?.workspaceId;
  const [pbs, setPbs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    window.__nx_api?.priceBooks?.list(wsId).then(r => setPbs(Array.isArray(r) ? r : r?.data || [])).catch(() => setPbs([])).finally(() => setLoading(false));
  }, [wsId]);

  useEffect(() => { load(); }, [load]);

  if (selected) return <PriceBookDetailView pb={selected} onBack={() => setSelected(null)} />;

  return (
    <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 700, fontSize: 15 }}>Price Books</span>
        <button style={btnPrimary} onClick={() => setShowForm(s => !s)}>+ New Price Book</button>
      </div>
      {showForm && <PriceBookForm onSave={() => { setShowForm(false); load(); }} onCancel={() => setShowForm(false)} />}
      {loading ? <div style={{ color: 'var(--nx-text-muted)', fontSize: 13 }}>Loading...</div> : pbs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--nx-text-muted)' }}>No price books yet</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
          {pbs.map(pb => <PriceBookCard key={pb.id} pb={pb} onView={setSelected} />)}
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════
   QuoteLineItemRow — editable inline
   ══════════════════════════════════════════════════════ */
const QuoteLineItemRow = ({ item, currency = 'USD', onUpdate, onRemove }) => {
  const [qty, setQty] = useState(item.quantity || 1);
  const [price, setPrice] = useState(item.unitPrice || 0);
  const [discount, setDiscount] = useState(item.discount || 0);
  const total = (qty * price * (1 - discount / 100));

  const commit = () => onUpdate?.({ ...item, quantity: Number(qty), unitPrice: Number(price), discount: Number(discount), total });

  return (
    <tr style={{ borderBottom: '1px solid var(--nx-border)' }}>
      <td style={{ padding: '6px 12px', fontSize: 12, fontWeight: 600 }}>{item.name || item.productName || '—'}</td>
      <td style={{ padding: '4px 8px' }}><input type="number" value={qty} onChange={e => setQty(e.target.value)} onBlur={commit} style={{ ...inputSx, width: 60 }} /></td>
      <td style={{ padding: '4px 8px' }}><input type="number" value={price} onChange={e => setPrice(e.target.value)} onBlur={commit} style={{ ...inputSx, width: 90 }} /></td>
      <td style={{ padding: '4px 8px' }}><input type="number" value={discount} onChange={e => setDiscount(e.target.value)} onBlur={commit} style={{ ...inputSx, width: 60 }} placeholder="%" /></td>
      <td style={{ padding: '6px 12px', fontSize: 12, fontWeight: 700, color: '#10b981' }}>{_fmt(total, currency)}</td>
      {onRemove && <td style={{ padding: '4px 8px' }}><button onClick={onRemove} style={{ ...btnGhost, padding: '2px 6px', color: '#ef4444', fontSize: 11 }}><NexusIcon name="Trash2" size={11} /></button></td>}
    </tr>
  );
};

/* ══════════════════════════════════════════════════════
   QuoteTotalsPanel
   ══════════════════════════════════════════════════════ */
const QuoteTotalsPanel = ({ items = [], currency = 'USD', taxRate = 0 }) => {
  const subtotal = items.reduce((s, i) => s + (Number(i.total) || Number(i.quantity) * Number(i.unitPrice) * (1 - (Number(i.discount) || 0) / 100) || 0), 0);
  const discountAmt = items.reduce((s, i) => s + (Number(i.quantity) * Number(i.unitPrice) * (Number(i.discount) || 0) / 100 || 0), 0);
  const tax = subtotal * (taxRate / 100);
  const total = subtotal + tax;

  const row = (label, val, bold) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: bold ? 14 : 12, fontWeight: bold ? 800 : 400, color: bold ? 'var(--nx-text)' : 'var(--nx-text-muted)', borderTop: bold ? '1px solid var(--nx-border)' : 'none', paddingTop: bold ? 8 : 4, marginTop: bold ? 4 : 0 }}>
      <span>{label}</span><span style={{ color: bold ? '#10b981' : undefined }}>{val}</span>
    </div>
  );

  return (
    <div style={{ ...card, padding: 16, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 240 }}>
      {row('Subtotal', _fmt(subtotal, currency))}
      {discountAmt > 0 && row('Discounts', `-${_fmt(discountAmt, currency)}`)}
      {taxRate > 0 && row(`Tax (${taxRate}%)`, _fmt(tax, currency))}
      {row('Total', _fmt(total, currency), true)}
    </div>
  );
};

/* ══════════════════════════════════════════════════════
   QuoteForm
   ══════════════════════════════════════════════════════ */
const QuoteForm = ({ onSave, onCancel }) => {
  const wsId = window.__nx_ws?.id || window.__nx_ws?.workspaceId;
  const store = useStore?.();
  const toast = useToast();
  const deals = useMemo(() => { const d = store?.deals || []; return Array.isArray(d) ? d : Object.values(d); }, [store?.deals]);
  const companies = useMemo(() => { const c = store?.companies || []; return Array.isArray(c) ? c : Object.values(c); }, [store?.companies]);
  const [f, setF] = useState({ title: '', dealId: '', companyId: '', validUntil: '', currency: 'USD', notes: '', terms: '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.title) { toast.show({ title: 'Title required', type: 'error' }); return; }
    setSaving(true);
    try {
      await window.__nx_api?.quotes?.create(wsId, f);
      toast.show({ title: 'Quote created', type: 'success' });
      onSave?.();
    } catch (e) { toast.show({ title: e.message, type: 'error' }); }
    setSaving(false);
  };

  return (
    <div style={{ ...card, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontWeight: 700, fontSize: 13 }}>New Quote</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div><label style={{ fontSize: 11, color: 'var(--nx-text-muted)', display: 'block', marginBottom: 3 }}>Title *</label><input style={inputSx} value={f.title} onChange={e => set('title', e.target.value)} /></div>
        <div><label style={{ fontSize: 11, color: 'var(--nx-text-muted)', display: 'block', marginBottom: 3 }}>Currency</label>
          <select style={inputSx} value={f.currency} onChange={e => set('currency', e.target.value)}>{CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
        <div><label style={{ fontSize: 11, color: 'var(--nx-text-muted)', display: 'block', marginBottom: 3 }}>Deal</label>
          <select style={inputSx} value={f.dealId} onChange={e => set('dealId', e.target.value)}>
            <option value="">Select deal...</option>
            {deals.map(d => <option key={d.id} value={d.id}>{d.title}</option>)}
          </select></div>
        <div><label style={{ fontSize: 11, color: 'var(--nx-text-muted)', display: 'block', marginBottom: 3 }}>Company</label>
          <select style={inputSx} value={f.companyId} onChange={e => set('companyId', e.target.value)}>
            <option value="">Select company...</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select></div>
        <div><label style={{ fontSize: 11, color: 'var(--nx-text-muted)', display: 'block', marginBottom: 3 }}>Valid Until</label><input type="date" style={inputSx} value={f.validUntil} onChange={e => set('validUntil', e.target.value)} /></div>
      </div>
      <div><label style={{ fontSize: 11, color: 'var(--nx-text-muted)', display: 'block', marginBottom: 3 }}>Notes</label><textarea style={{ ...inputSx, resize: 'vertical', minHeight: 50 }} value={f.notes} onChange={e => set('notes', e.target.value)} /></div>
      <div><label style={{ fontSize: 11, color: 'var(--nx-text-muted)', display: 'block', marginBottom: 3 }}>Terms &amp; Conditions</label><textarea style={{ ...inputSx, resize: 'vertical', minHeight: 50 }} value={f.terms} onChange={e => set('terms', e.target.value)} /></div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        {onCancel && <button style={btnGhost} onClick={onCancel}>Cancel</button>}
        <button style={btnPrimary} onClick={submit} disabled={saving}>{saving ? 'Creating...' : 'Create Quote'}</button>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════
   QuoteDetailView
   ══════════════════════════════════════════════════════ */
const QuoteDetailView = ({ quote, onBack, onStatusChange }) => {
  const wsId = window.__nx_ws?.id || window.__nx_ws?.workspaceId;
  const toast = useToast();
  const [items, setItems] = useState(quote.lineItems || []);
  const [showAdd, setShowAdd] = useState(false);
  const [newItem, setNewItem] = useState({ name: '', quantity: 1, unitPrice: '', discount: 0 });
  const [adding, setAdding] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    window.__nx_api?.quotes?.getWithLineItems(wsId, quote.id)
      .then(r => { if (r?.lineItems) setItems(r.lineItems); })
      .catch(() => {});
  }, [quote.id, wsId]);

  const addItem = async () => {
    if (!newItem.name || !newItem.unitPrice) { toast.show({ title: 'Name and unit price required', type: 'error' }); return; }
    setAdding(true);
    try {
      const i = await window.__nx_api?.quotes?.addLineItem(wsId, quote.id, { ...newItem, qty: Number(newItem.quantity), unitPrice: Number(newItem.unitPrice) });
      setItems(prev => [...prev, i || { ...newItem, id: Date.now() }]);
      setNewItem({ name: '', quantity: 1, unitPrice: '', discount: 0 });
      setShowAdd(false);
    } catch (e) { toast.show({ title: e.message, type: 'error' }); }
    setAdding(false);
  };

  const updateItem = async (data) => {
    if (!wsId || !data?.id) return;
    try {
      const updated = await window.__nx_api?.quotes?.updateLineItem(wsId, quote.id, data.id, {
        qty: data.quantity,
        unitPrice: data.unitPrice,
        discount: data.discount,
      });
      if (updated) setItems(prev => prev.map(i => i.id === data.id ? { ...i, ...updated } : i));
    } catch (e) { toast.show({ title: e.message || 'Failed to update line item', type: 'error' }); }
  };

  const removeItem = async (id) => {
    try {
      await window.__nx_api?.quotes?.removeLineItem(wsId, quote.id, id);
      setItems(prev => prev.filter(x => x.id !== id));
    } catch (e) { toast.show({ title: e.message, type: 'error' }); }
  };

  const changeStatus = async (status) => {
    setUpdating(true);
    try {
      await window.__nx_api?.quotes?.updateStatus(wsId, quote.id, status);
      toast.show({ title: `Quote ${status.toLowerCase()}`, type: 'success' });
      onStatusChange?.(status);
    } catch (e) { toast.show({ title: e.message, type: 'error' }); }
    setUpdating(false);
  };

  return (
    <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button style={{ ...btnGhost, padding: '4px 10px', fontSize: 11 }} onClick={onBack}><NexusIcon name="ChevronLeft" size={12} /> Back</button>
        <span style={{ fontWeight: 700, fontSize: 15 }}>{quote.title}</span>
        <StatusPill status={quote.status || 'DRAFT'} />
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {quote.status === 'DRAFT' && <button style={{ ...btnPrimary, fontSize: 11, padding: '5px 12px' }} onClick={() => changeStatus('SENT')} disabled={updating}>Send</button>}
          {quote.status === 'SENT' && <button style={{ ...btnPrimary, fontSize: 11, padding: '5px 12px', background: '#10b981' }} onClick={() => changeStatus('APPROVED')} disabled={updating}>Approve</button>}
          <button style={{ ...btnGhost, fontSize: 11, padding: '5px 12px' }} onClick={() => toast.show({ title: 'PDF export coming soon', type: 'info' })}>
            <NexusIcon name="Download" size={12} style={{ marginRight: 4 }} />PDF
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, fontSize: 12 }}>
        {quote.dealTitle && <div style={card}><div style={{ padding: '8px 12px' }}><div style={{ fontSize: 10, color: 'var(--nx-text-muted)', fontWeight: 700, marginBottom: 2 }}>DEAL</div><div style={{ fontWeight: 600 }}>{quote.dealTitle}</div></div></div>}
        {quote.companyName && <div style={card}><div style={{ padding: '8px 12px' }}><div style={{ fontSize: 10, color: 'var(--nx-text-muted)', fontWeight: 700, marginBottom: 2 }}>COMPANY</div><div style={{ fontWeight: 600 }}>{quote.companyName}</div></div></div>}
        {quote.validUntil && <div style={card}><div style={{ padding: '8px 12px' }}><div style={{ fontSize: 10, color: 'var(--nx-text-muted)', fontWeight: 700, marginBottom: 2 }}>VALID UNTIL</div><div style={{ fontWeight: 600 }}>{_date(quote.validUntil)}</div></div></div>}
      </div>

      <div style={{ ...card, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--nx-surface)' }}>
              {['Product / Name', 'Qty', 'Unit Price', 'Discount %', 'Total', ''].map(h => (
                <th key={h} style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid var(--nx-border)', color: 'var(--nx-text-muted)', fontSize: 10, fontWeight: 700 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: 20, textAlign: 'center', color: 'var(--nx-text-muted)', fontSize: 12 }}>No line items yet</td></tr>
            ) : items.map(i => <QuoteLineItemRow key={i.id} item={i} currency={quote.currency} onUpdate={updateItem} onRemove={() => removeItem(i.id)} />)}
          </tbody>
        </table>
      </div>

      {showAdd ? (
        <div style={{ ...card, padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontWeight: 700, fontSize: 12 }}>Add Line Item</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
            <input style={inputSx} placeholder="Name *" value={newItem.name} onChange={e => setNewItem(p => ({ ...p, name: e.target.value }))} />
            <input type="number" style={inputSx} placeholder="Qty" value={newItem.quantity} onChange={e => setNewItem(p => ({ ...p, quantity: e.target.value }))} />
            <input type="number" style={inputSx} placeholder="Unit price *" value={newItem.unitPrice} onChange={e => setNewItem(p => ({ ...p, unitPrice: e.target.value }))} />
            <input type="number" style={inputSx} placeholder="Discount %" value={newItem.discount} onChange={e => setNewItem(p => ({ ...p, discount: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={btnGhost} onClick={() => setShowAdd(false)}>Cancel</button>
            <button style={btnPrimary} onClick={addItem} disabled={adding}>{adding ? 'Adding...' : 'Add'}</button>
          </div>
        </div>
      ) : <button style={{ ...btnGhost, alignSelf: 'flex-start' }} onClick={() => setShowAdd(true)}>+ Add Line Item</button>}

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <QuoteTotalsPanel items={items} currency={quote.currency} />
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════
   QuoteCard
   ══════════════════════════════════════════════════════ */
const QuoteCard = ({ quote, onView }) => (
  <div style={{ ...card, padding: 14, display: 'flex', flexDirection: 'column', gap: 8, cursor: 'pointer' }} onClick={() => onView?.(quote)}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
      <div>
        <div style={{ fontSize: 10, color: 'var(--nx-text-muted)', fontWeight: 700 }}>#{quote.quoteNumber || quote.id?.toString().slice(-6)}</div>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>{quote.title}</div>
        <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>{quote.companyName || quote.dealTitle || '—'}</div>
      </div>
      <StatusPill status={quote.status || 'DRAFT'} />
    </div>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ fontSize: 13, fontWeight: 700, color: '#10b981' }}>{_fmt(quote.total, quote.currency)}</span>
      <span style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>{_date(quote.createdAt)}</span>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════
   QuotesView
   ══════════════════════════════════════════════════════ */
const QuotesView = () => {
  const wsId = window.__nx_ws?.id || window.__nx_ws?.workspaceId;
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    window.__nx_api?.quotes?.list(wsId).then(r => setQuotes(Array.isArray(r) ? r : r?.data || [])).catch(() => setQuotes([])).finally(() => setLoading(false));
  }, [wsId]);

  useEffect(() => { load(); }, [load]);

  if (selected) return <QuoteDetailView quote={selected} onBack={() => setSelected(null)} onStatusChange={(s) => setSelected(q => ({ ...q, status: s }))} />;

  const filtered = statusFilter ? quotes.filter(q => q.status === statusFilter) : quotes;

  return (
    <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <span style={{ fontWeight: 700, fontSize: 15 }}>Quotes</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <select style={{ ...inputSx, width: 130 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All Statuses</option>
            {QUOTE_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <button style={btnPrimary} onClick={() => setShowForm(s => !s)}>+ New Quote</button>
        </div>
      </div>
      {showForm && <QuoteForm onSave={() => { setShowForm(false); load(); }} onCancel={() => setShowForm(false)} />}
      {loading ? <div style={{ color: 'var(--nx-text-muted)', fontSize: 13 }}>Loading...</div> : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--nx-text-muted)' }}>No quotes found</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
          {filtered.map(q => <QuoteCard key={q.id} quote={q} onView={setSelected} />)}
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════
   CommerceHubView — 3-tab container
   ══════════════════════════════════════════════════════ */
const CommerceHubView = () => {
  const [tab, setTab] = useState('catalog');
  const tabs = [
    { id: 'catalog', label: 'Product Catalog', icon: 'Package' },
    { id: 'pricebooks', label: 'Price Books', icon: 'Tag' },
    { id: 'quotes', label: 'Quotes', icon: 'FileText' },
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ display: 'flex', borderBottom: '1px solid var(--nx-border)', paddingLeft: 20 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: tab === t.id ? 'var(--nx-accent)' : 'var(--nx-text-muted)', borderBottom: tab === t.id ? '2px solid var(--nx-accent)' : '2px solid transparent', marginBottom: -1 }}>
            <NexusIcon name={t.icon} size={13} /> {t.label}
          </button>
        ))}
      </div>
      <div style={{ flex: 1, overflow: 'auto' }}>
        {tab === 'catalog' && <ProductCatalogView />}
        {tab === 'pricebooks' && <PriceBooksView />}
        {tab === 'quotes' && <QuotesView />}
      </div>
    </div>
  );
};

/* ── window registrations ── */
window.ProductCatalogView = ProductCatalogView;
window.PriceBooksView     = PriceBooksView;
window.QuotesView         = QuotesView;
window.CommerceHubView    = CommerceHubView;
